import { toast } from '@heroui/react';
import { useState, useEffect } from 'react';
import { useForm, useFieldArray, type Path } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import {
  Button,
  Card,
  Input,
  Label,
  Radio,
  RadioGroup,
} from '@heroui/react';
import { trpc } from '@/lib/trpc';
import { IconPlus as Plus, IconTrash as Trash2, IconPencil as Edit2 } from '@tabler/icons-react';

type WeChatMenuButton = {
  name: string;
  type?: 'click' | 'view' | 'scancode_push' | 'scancode_waitmsg' | 'pic_sysphoto' | 'pic_photo_or_album' | 'pic_weixin' | 'location_select' | 'media_id' | 'view_limited' | 'miniprogram';
  key?: string;
  url?: string;
  media_id?: string;
  appid?: string;
  pagepath?: string;
  sub_button?: WeChatMenuButton[];
};

type MenuType = 'directory' | 'action';

const ACTION_TYPES = [
  { value: 'click', label: '点击推事件' },
  { value: 'view', label: '跳转网页' },
  { value: 'scancode_push', label: '扫码推事件' },
  { value: 'scancode_waitmsg', label: '扫码带提示' },
  { value: 'pic_sysphoto', label: '系统拍照发图' },
  { value: 'pic_photo_or_album', label: '拍照或者相册发图' },
  { value: 'pic_weixin', label: '微信相册发图' },
  { value: 'location_select', label: '发送位置' },
  { value: 'media_id', label: '图片/视频消息' },
  { value: 'view_limited', label: '跳转图文消息' },
  { value: 'miniprogram', label: '跳转小程序' },
] as const;

const subMenuSchema = z.object({
  name: z.string().min(1, '请输入菜单名称').max(16, '菜单名称最多16个字符'),
  type: z.enum([
    'click',
    'view',
    'scancode_push',
    'scancode_waitmsg',
    'pic_sysphoto',
    'pic_photo_or_album',
    'pic_weixin',
    'location_select',
    'media_id',
    'view_limited',
    'miniprogram',
  ]),
  key: z.string().optional(),
  url: z.string().optional(),
  media_id: z.string().optional(),
  appid: z.string().optional(),
  pagepath: z.string().optional(),
});

const menuFormSchema = z.object({
  menuType: z.enum(['directory', 'action']),
  name: z.string().min(1, '请输入菜单名称').max(16, '菜单名称最多16个字符'),
  actionType: z.enum([
    'click',
    'view',
    'scancode_push',
    'scancode_waitmsg',
    'pic_sysphoto',
    'pic_photo_or_album',
    'pic_weixin',
    'location_select',
    'media_id',
    'view_limited',
    'miniprogram',
  ]).optional(),
  key: z.string().optional(),
  url: z.string().optional(),
  media_id: z.string().optional(),
  appid: z.string().optional(),
  pagepath: z.string().optional(),
  subMenus: z.array(subMenuSchema).optional(),
});

type MenuFormData = z.infer<typeof menuFormSchema>;

/**
 * 菜单管理页面
 */
export default function MenuManagementPage() {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [menus, setMenus] = useState<WeChatMenuButton[]>([]);

  const { data: menuData, isLoading, refetch } = trpc.wechat.getMenu.useQuery();
  const createMenuMutation = trpc.wechat.createMenu.useMutation({
    onSuccess: () => {
      toast.success('菜单保存成功');
      refetch();
    },
    onError: (error) => {
      toast.danger(`菜单保存失败: ${error.message}`);
    },
  });
  const deleteMenuMutation = trpc.wechat.deleteMenu.useMutation({
    onSuccess: () => {
      toast.success('菜单删除成功');
      refetch();
    },
    onError: (error) => {
      toast.danger(`菜单删除失败: ${error.message}`);
    },
  });

  const form = useForm<MenuFormData>({
    resolver: zodResolver(menuFormSchema),
    defaultValues: {
      menuType: 'action',
      name: '',
      subMenus: [],
    },
  });

  const { fields: subMenuFields, append: appendSubMenu, remove: removeSubMenu } = useFieldArray({
    control: form.control,
    name: 'subMenus',
  });

  const menuType = form.watch('menuType');
  const actionType = form.watch('actionType');

  useEffect(() => {
    if (menuData?.menu?.button) {
      setMenus(menuData.menu.button);
    }
  }, [menuData]);

  const handleAddMenu = () => {
    form.reset({
      menuType: 'action',
      name: '',
      subMenus: [],
    });
    setEditingIndex(null);
  };

  const handleEditMenu = (index: number) => {
    const menu = menus[index];
    if (!menu) return;

    const isDirectory = menu.sub_button && menu.sub_button.length > 0;

    form.reset({
      menuType: isDirectory ? 'directory' : 'action',
      name: menu.name,
      actionType: menu.type,
      key: menu.key,
      url: menu.url,
      media_id: menu.media_id,
      appid: menu.appid,
      pagepath: menu.pagepath,
      subMenus: menu.sub_button || [],
    });

    setEditingIndex(index);
  };

  const handleDeleteMenu = async (index: number) => {
    if (!confirm('确定要删除这个菜单吗？')) return;

    const newMenus = menus.filter((_, i) => i !== index);
    if (newMenus.length === 0) {
      await deleteMenuMutation.mutateAsync();
      setEditingIndex(null);
      form.reset({
        menuType: 'action',
        name: '',
        subMenus: [],
      });
    } else {
      await saveMenus(newMenus);
      setEditingIndex(null);
      form.reset({
        menuType: 'action',
        name: '',
        subMenus: [],
      });
    }
  };

  const saveMenus = async (newMenus: WeChatMenuButton[]) => {
    await createMenuMutation.mutateAsync({
      button: newMenus,
    });
  };

  const onSubmit = async (data: MenuFormData) => {
    try {
      let menuButton: WeChatMenuButton;

      if (data.menuType === 'directory') {
        if (!data.subMenus || data.subMenus.length === 0) {
          toast.danger('目录类型必须至少添加一个子菜单');
          return;
        }

        for (const sub of data.subMenus) {
          if (sub.type === 'click' || sub.type === 'scancode_push' || sub.type === 'scancode_waitmsg' ||
              sub.type === 'pic_sysphoto' || sub.type === 'pic_photo_or_album' || sub.type === 'pic_weixin' ||
              sub.type === 'location_select') {
            if (!sub.key) {
              toast.danger(`子菜单"${sub.name}"的${ACTION_TYPES.find(a => a.value === sub.type)?.label}需要填写KEY值`);
              return;
            }
          } else if (sub.type === 'view') {
            if (!sub.url) {
              toast.danger(`子菜单"${sub.name}"的跳转网页需要填写URL`);
              return;
            }
          } else if (sub.type === 'media_id' || sub.type === 'view_limited') {
            if (!sub.media_id) {
              toast.danger(`子菜单"${sub.name}"需要填写素材ID`);
              return;
            }
          } else if (sub.type === 'miniprogram') {
            if (!sub.appid || !sub.pagepath) {
              toast.danger(`子菜单"${sub.name}"的跳转小程序需要填写AppID和页面路径`);
              return;
            }
          }
        }

        menuButton = {
          name: data.name,
          sub_button: data.subMenus.map((sub) => ({
            name: sub.name,
            type: sub.type,
            ...(sub.type === 'click' && sub.key && { key: sub.key }),
            ...(sub.type === 'view' && sub.url && { url: sub.url }),
            ...(sub.type === 'scancode_push' && sub.key && { key: sub.key }),
            ...(sub.type === 'scancode_waitmsg' && sub.key && { key: sub.key }),
            ...(sub.type === 'pic_sysphoto' && sub.key && { key: sub.key }),
            ...(sub.type === 'pic_photo_or_album' && sub.key && { key: sub.key }),
            ...(sub.type === 'pic_weixin' && sub.key && { key: sub.key }),
            ...(sub.type === 'location_select' && sub.key && { key: sub.key }),
            ...(sub.type === 'media_id' && sub.media_id && { media_id: sub.media_id }),
            ...(sub.type === 'view_limited' && sub.media_id && { media_id: sub.media_id }),
            ...(sub.type === 'miniprogram' && sub.appid && sub.pagepath && {
              appid: sub.appid,
              pagepath: sub.pagepath,
            }),
          })),
        };
      } else {
        if (!data.actionType) {
          toast.danger('请选择动作类型');
          return;
        }

        if (data.actionType === 'click' || data.actionType === 'scancode_push' || data.actionType === 'scancode_waitmsg' ||
            data.actionType === 'pic_sysphoto' || data.actionType === 'pic_photo_or_album' || data.actionType === 'pic_weixin' ||
            data.actionType === 'location_select') {
          if (!data.key) {
            toast.danger(`${ACTION_TYPES.find(a => a.value === data.actionType)?.label}需要填写KEY值`);
            return;
          }
        } else if (data.actionType === 'view') {
          if (!data.url) {
            toast.danger('跳转网页需要填写URL');
            return;
          }
        } else if (data.actionType === 'media_id' || data.actionType === 'view_limited') {
          if (!data.media_id) {
            toast.danger('需要填写素材ID');
            return;
          }
        } else if (data.actionType === 'miniprogram') {
          if (!data.appid || !data.pagepath) {
            toast.danger('跳转小程序需要填写AppID和页面路径');
            return;
          }
        }

        menuButton = {
          name: data.name,
          type: data.actionType,
          ...(data.actionType === 'click' && data.key && { key: data.key }),
          ...(data.actionType === 'view' && data.url && { url: data.url }),
          ...(data.actionType === 'scancode_push' && data.key && { key: data.key }),
          ...(data.actionType === 'scancode_waitmsg' && data.key && { key: data.key }),
          ...(data.actionType === 'pic_sysphoto' && data.key && { key: data.key }),
          ...(data.actionType === 'pic_photo_or_album' && data.key && { key: data.key }),
          ...(data.actionType === 'pic_weixin' && data.key && { key: data.key }),
          ...(data.actionType === 'location_select' && data.key && { key: data.key }),
          ...(data.actionType === 'media_id' && data.media_id && { media_id: data.media_id }),
          ...(data.actionType === 'view_limited' && data.media_id && { media_id: data.media_id }),
          ...(data.actionType === 'miniprogram' && data.appid && data.pagepath && {
            appid: data.appid,
            pagepath: data.pagepath,
          }),
        };
      }

      const newMenus = editingIndex !== null
        ? menus.map((m, i) => (i === editingIndex ? menuButton : m))
        : [...menus, menuButton];

      if (newMenus.length > 3) {
        toast.danger('最多只能添加3个一级菜单');
        return;
      }

      await saveMenus(newMenus);
      form.reset({
        menuType: 'action',
        name: '',
        subMenus: [],
      });
      setEditingIndex(null);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddSubMenu = () => {
    appendSubMenu({
      name: '',
      type: 'click',
      key: '',
    });
  };

  const renderActionValueInput = (isSubMenu = false, subIndex?: number) => {
    if (isSubMenu && (subIndex === undefined || subIndex === null)) return null;

    const currentActionType = isSubMenu
      ? form.watch(`subMenus.${subIndex!}.type`)
      : actionType;

    if (!currentActionType) return null;

    const basePath = isSubMenu ? `subMenus.${subIndex!}` : '';

    type FormPath = Path<MenuFormData>;

    switch (currentActionType) {
      case 'click':
      case 'scancode_push':
      case 'scancode_waitmsg':
      case 'pic_sysphoto':
      case 'pic_photo_or_album':
      case 'pic_weixin':
      case 'location_select':
        return (
          <div className="space-y-2">
            <Label className="text-sm font-medium">菜单 KEY 值</Label>
            <Input
              {...form.register(`${basePath}.key` as FormPath)}
              placeholder="请输入KEY值"
            />
            <p className="text-sm text-muted-foreground">
              服务器接收到此 KEY 后可触发自动回复消息
            </p>
          </div>
        );

      case 'view':
        return (
          <div className="space-y-2">
            <Label className="text-sm font-medium">网页链接</Label>
            <Input
              {...form.register(`${basePath}.url` as FormPath)}
              placeholder="请输入网页链接"
              type="url"
            />
          </div>
        );

      case 'media_id':
      case 'view_limited':
        return (
          <div className="space-y-2">
            <Label className="text-sm font-medium">素材 ID</Label>
            <Input
              {...form.register(`${basePath}.media_id` as FormPath)}
              placeholder="请输入素材ID"
            />
          </div>
        );

      case 'miniprogram':
        return (
          <div className="space-y-2">
            <Label className="text-sm font-medium">小程序 AppID</Label>
            <Input
              {...form.register(`${basePath}.appid` as FormPath)}
              placeholder="请输入小程序AppID"
            />
            <Label className="mt-2 text-sm font-medium">小程序页面路径</Label>
            <Input
              {...form.register(`${basePath}.pagepath` as FormPath)}
              placeholder="请输入页面路径"
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">公众号菜单管理</h2>
        <Button
          variant="primary"
          onPress={handleAddMenu}
          isDisabled={menus.length >= 3 || isLoading || createMenuMutation.isPending}
        >
          <Plus className="mr-2 size-4" />
          添加菜单
        </Button>
      </div>

      {isLoading && (
        <div className="text-center py-4 text-muted-foreground">
          加载中...
        </div>
      )}

      {menus.length > 0 && (
        <Card>
          <Card.Content className="p-6">
            <h3 className="mb-4 text-lg font-semibold leading-none">当前菜单</h3>
            <div className="flex flex-wrap gap-2">
              {menus.map((menu, index) => (
                <div
                  key={index}
                  role="button"
                  tabIndex={0}
                  className="flex cursor-pointer items-center gap-2 rounded-lg border border-divider p-3 hover:bg-accent"
                  onClick={() => handleEditMenu(index)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleEditMenu(index);
                    }
                  }}
                >
                  <span className="font-medium">{menu.name}</span>
                  {menu.sub_button && menu.sub_button.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      ({menu.sub_button.length} 个子菜单)
                    </span>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    isIconOnly
                    aria-label="编辑"
                    onPress={() => handleEditMenu(index)}
                  >
                    <Edit2 className="size-4" />
                  </Button>
                </div>
              ))}
            </div>
          </Card.Content>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-6">
        <div>
          <Card>
            <Card.Content className="p-6">
              <h3 className="mb-4 text-lg font-semibold leading-none">预览</h3>
              <PhonePreview menus={menus} />
            </Card.Content>
          </Card>
        </div>

        <div>
          <Card>
            <Card.Content className="p-6">
              <h3 className="mb-4 text-lg font-semibold leading-none">
                {editingIndex !== null ? '编辑菜单' : '添加菜单'}
              </h3>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">菜单类型</Label>
                  <RadioGroup
                    value={menuType}
                    onChange={(value) => {
                      form.setValue('menuType', value as MenuType);
                      if (
                        value === 'directory' &&
                        (!form.watch('subMenus') || form.watch('subMenus')?.length === 0)
                      ) {
                        handleAddSubMenu();
                      }
                    }}
                    orientation="horizontal"
                    className="flex gap-6"
                  >
                    <Radio value="directory">
                      <Radio.Control>
                        <Radio.Indicator />
                      </Radio.Control>
                      <Radio.Content>
                        <Label className="cursor-pointer">目录</Label>
                      </Radio.Content>
                    </Radio>
                    <Radio value="action">
                      <Radio.Control>
                        <Radio.Indicator />
                      </Radio.Control>
                      <Radio.Content>
                        <Label className="cursor-pointer">动作</Label>
                      </Radio.Content>
                    </Radio>
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">菜单名称</Label>
                  <Input
                    {...form.register('name')}
                    placeholder="请输入菜单名称"
                    maxLength={16}
                  />
                  {form.formState.errors.name && (
                    <p className="text-danger text-sm">{form.formState.errors.name.message}</p>
                  )}
                </div>

                {menuType === 'directory' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">下级子菜单</Label>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onPress={handleAddSubMenu}
                        isDisabled={subMenuFields.length >= 5}
                      >
                        <Plus className="mr-2 size-4" />
                        添加子菜单
                      </Button>
                    </div>

                    {subMenuFields.map((field, index) => (
                      <Card key={field.id}>
                        <Card.Content className="space-y-4 p-4">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium">子菜单 {index + 1}</Label>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              isIconOnly
                              aria-label="删除子菜单"
                              onPress={() => removeSubMenu(index)}
                            >
                              <Trash2 className="size-4" />
                            </Button>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-sm font-medium">菜单名称</Label>
                            <Input
                              {...form.register(`subMenus.${index}.name`)}
                              placeholder="请输入菜单名称"
                              maxLength={16}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label className="text-sm font-medium">动作类型</Label>
                            <div className="grid grid-cols-3 gap-2">
                              {ACTION_TYPES.map((action) => (
                                <Button
                                  key={action.value}
                                  type="button"
                                  variant={
                                    form.watch(`subMenus.${index}.type`) === action.value
                                      ? 'primary'
                                      : 'secondary'
                                  }
                                  size="sm"
                                  onPress={() => {
                                    form.setValue(`subMenus.${index}.type`, action.value);
                                    form.setValue(`subMenus.${index}.key`, '');
                                    form.setValue(`subMenus.${index}.url`, '');
                                    form.setValue(`subMenus.${index}.media_id`, '');
                                    form.setValue(`subMenus.${index}.appid`, '');
                                    form.setValue(`subMenus.${index}.pagepath`, '');
                                  }}
                                >
                                  {action.label}
                                </Button>
                              ))}
                            </div>
                          </div>

                          {renderActionValueInput(true, index)}
                        </Card.Content>
                      </Card>
                    ))}
                  </div>
                )}

                {menuType === 'action' && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">动作类型</Label>
                      <div className="grid grid-cols-3 gap-2">
                        {ACTION_TYPES.map((action) => (
                          <Button
                            key={action.value}
                            type="button"
                            variant={actionType === action.value ? 'primary' : 'secondary'}
                            size="sm"
                            onPress={() => {
                              form.setValue('actionType', action.value);
                              form.setValue('key', '');
                              form.setValue('url', '');
                              form.setValue('media_id', '');
                              form.setValue('appid', '');
                              form.setValue('pagepath', '');
                            }}
                          >
                            {action.label}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {renderActionValueInput()}
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    type="submit"
                    variant="primary"
                    isDisabled={createMenuMutation.isPending || deleteMenuMutation.isPending}
                  >
                    {createMenuMutation.isPending ? '保存中...' : editingIndex !== null ? '保存' : '添加'}
                  </Button>
                  {editingIndex !== null && (
                    <Button
                      type="button"
                      variant="danger"
                      onPress={() => void handleDeleteMenu(editingIndex)}
                      isDisabled={createMenuMutation.isPending || deleteMenuMutation.isPending}
                    >
                      <Trash2 className="mr-2 size-4" />
                      {deleteMenuMutation.isPending ? '删除中...' : '删除'}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="secondary"
                    onPress={() => {
                      form.reset({
                        menuType: 'action',
                        name: '',
                        subMenus: [],
                      });
                      setEditingIndex(null);
                    }}
                  >
                    取消
                  </Button>
                </div>
              </form>
            </Card.Content>
          </Card>
        </div>
      </div>
    </div>
  );
}

function PhonePreview({ menus }: { menus: WeChatMenuButton[] }) {
  return (
    <div className="flex justify-center">
      <div className="relative w-[320px] h-[640px] bg-gray-900 rounded-[40px] p-2 shadow-2xl">
        <div className="w-full h-full bg-white rounded-[32px] overflow-hidden flex flex-col">
          <div className="h-8 bg-gray-100 flex items-center justify-between px-4 text-xs">
            <span>9:41</span>
            <div className="flex gap-1">
              <div className="w-4 h-2 border border-gray-600 rounded-sm">
                <div className="w-3 h-full bg-gray-600 rounded-sm" />
              </div>
            </div>
          </div>

          <div className="h-12 bg-white border-b flex items-center justify-between px-4">
            <button type="button" className="text-gray-600">←</button>
            <span className="text-sm font-medium">微信公众号</span>
            <button type="button" className="text-gray-600">⋮</button>
          </div>

          <div className="flex-1 bg-gray-50 flex items-center justify-center p-4">
            <p className="text-sm text-gray-400 text-center">
              这里将显示公众号接收到的消息
            </p>
          </div>

          <div className="h-16 bg-white border-t flex items-center justify-center gap-1 px-2">
            {menus.length === 0 ? (
              <button type="button" className="flex-1 h-12 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-gray-400">
                <Plus className="h-6 w-6" />
              </button>
            ) : (
              <>
                {menus.map((menu, index) => (
                  <button
                    key={index}
                    type="button"
                    className="flex-1 h-12 border border-gray-200 rounded flex items-center justify-center text-sm hover:bg-gray-50 px-1"
                    title={menu.name}
                  >
                    <span className="truncate">{menu.name}</span>
                  </button>
                ))}
                {menus.length < 3 && (
                  <button type="button" className="flex-1 h-12 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-gray-400">
                    <Plus className="h-6 w-6" />
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
